"use client";

import {
  ChakraProvider,
  createSystem,
  defaultConfig,
  defineConfig,
} from "@chakra-ui/react";
import * as React from "react";
import type { PropsWithChildren } from "react";

import buttonRecipe from "./recipes/button";
import dialogRecipe from "./recipes/dialog";
import inputRecipe from "./recipes/input";
import menuRecipe from "./recipes/menu";
import selectRecipe from "./recipes/select";
import textareaRecipe from "./recipes/textarea";

const FONT_FALLBACK = `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;

const config = defineConfig({
  strictTokens: true,
  conditions: {
    addie: "[data-brand='addie'] &",
  },
  globalCss: {
    body: {
      background: "bg.page",
      color: "fg",
      colorPalette: "blue",
      fontFamily: "body",
    },
  },
  theme: {
    tokens: {
      borderWidths: {
        0: { value: "0px" },
        1: { value: "1px" },
        2: { value: "2px" },
      },
      colors: {
        octaneAccent: {
          DEFAULT: { value: "#4988F5" },
          950: { value: "#4988F5E5" },
        },
        addieAccent: {
          DEFAULT: { value: "#6B7D2E" },
          950: { value: "#6B7D2EE5" },
        },
        inherit: { value: "inherit" },
        octaneNavy: {
          DEFAULT: { value: "#09255B" },
          950: { value: "#3B6DC3" },
        },
        addieNavy: {
          DEFAULT: { value: "#1B1F3B" },
          950: { value: "#5B6CCD" },
        },
        pageBg: { value: "rgb(253,253,253)" },
        inputRing: { value: "rgb(226,232,240)" },
        text2: { value: "rgb(65,70,81)" },
        faded: { value: "rgb(173,173,183)" },
        crumb: { value: "rgb(183,183,189)" },
        tableHead: { value: "rgb(83,88,98)" },
        sky: { value: "rgb(69,195,255)" },
        brandOrange: { value: "rgb(246,119,32)" },
        brand: {
          linkedin: { value: "rgb(10,102,194)" },
          meta: { value: "rgb(0,129,251)" },
          hubspot: { value: "#FF7A59" },
          shopify: { value: "#95BF47" },
        },
        meterTrack: { value: "rgb(233,236,241)" },
        overlay: { value: "rgba(9,9,11,0.4)" },
        chart: {
          blue: { value: "rgb(11,117,241)" },
          deep: { value: "rgb(4,46,255)" },
          areaTop: { value: "rgb(190,243,255)" },
          grid: { value: "rgba(218,218,218,0.5)" },
          band: { value: "rgba(73,136,245,0.08)" },
          spendBar: { value: "rgba(73,136,245,0.16)" },
        },
        pill: {
          green: {
            bg: { value: "rgb(242,255,247)" },
            ring: { value: "rgb(224,249,244)" },
            text: { value: "rgb(32,120,106)" },
            dot: { value: "rgb(66,192,172)" },
          },
          blue: {
            bg: { value: "rgb(215,237,255)" },
            ring: { value: "rgb(198,228,252)" },
            text: { value: "rgb(14,115,246)" },
          },
          amber: {
            bg: { value: "rgb(255,248,240)" },
            ring: { value: "rgb(253,234,214)" },
            text: { value: "rgb(194,98,23)" },
          },
          red: {
            bg: { value: "rgb(255,245,243)" },
            ring: { value: "rgb(253,226,221)" },
            text: { value: "rgb(199,62,41)" },
          },
          gray: {
            bg: { value: "rgb(250,250,250)" },
            ring: { value: "rgb(228,228,231)" },
            text: { value: "rgb(113,113,122)" },
          },
          iris: {
            bg: { value: "rgb(230,231,255)" },
            text: { value: "rgb(88,86,214)" },
          },
          yellow: {
            bg: { value: "rgb(254,251,232)" },
            ring: { value: "rgb(250,238,170)" },
            text: { value: "rgb(164,128,10)" },
            dot: { value: "rgb(234,179,8)" },
          },
        },
        warn: {
          bg: { value: "rgb(255,251,235)" },
          text: { value: "rgb(180,130,10)" },
        },
      },
      fonts: {
        body: { value: `'Outfit', ${FONT_FALLBACK}` },
        heading: { value: `'Outfit', ${FONT_FALLBACK}` },
        inter: { value: `'Inter', ${FONT_FALLBACK}` },
        poppins: { value: `'Poppins', ${FONT_FALLBACK}` },
      },
      opacity: {
        0: { value: "0" },
      },
      spacing: {
        0: { value: "0" },
      },
      sizes: {
        auto: { value: "auto" },
        0: { value: "0" },
      },
      gradients: {
        icon: {
          circle: {
            DEFAULT: {
              value: `
linear-gradient(130.5deg, #3F7FFB 17.9%, #88DAFF 115.24%)
`,
            },

            flat: {
              value: "#88DAFF",
            },
          },
        },
        navActive: {
          value: `
linear-gradient(90deg, rgba(69,195,255,0) 0%, rgba(9,37,91,0.2) 100%),
linear-gradient(rgba(245,252,255,0.07), rgba(245,252,255,0.07))
`,
        },
      },
      shadows: {
        none: { value: "none" },
        xs: { value: "0px 1px 2px 0px rgba(0,0,0,0.05)" },
        card: {
          value:
            "inset 0 0 0 1px {colors.gray.200}, 0px 1px 2px 0px rgba(0,0,0,0.05)",
        },
        table: {
          value:
            "inset 0 0 0 1px {colors.gray.200}, 0px 1px 3px 0px rgba(10,13,18,0.1)",
        },
        menu: {
          value:
            "inset 0 0 0 1px {colors.gray.200}, 0px 2px 4px -2px rgba(0,0,0,0.1), 0px 4px 6px -1px rgba(0,0,0,0.1)",
        },
        dialog: {
          value:
            "inset 0 0 0 1px {colors.gray.200}, 0px 4px 6px -4px rgba(0,0,0,0.1), 0px 10px 15px -3px rgba(0,0,0,0.1)",
        },
        input: {
          value:
            "0 0 0 1px {colors.inputRing}, 0px 1px 2px 0px rgba(0,0,0,0.05)",
        },
        insetRing: {
          value: "inset 0 0 0 1px {colors.gray.200}",
        },
        choiceHover: {
          value: "inset 0 0 0 1px rgb(203,206,212)",
        },
        pill: {
          green: {
            value:
              "inset 0 0 0 1px {colors.pill.green.ring}, 0px 1px 2px 0px rgba(0,0,0,0.05)",
          },
          blue: {
            value:
              "inset 0 0 0 1px {colors.pill.blue.ring}, 0px 1px 2px 0px rgba(0,0,0,0.05)",
          },
          amber: {
            value:
              "inset 0 0 0 1px {colors.pill.amber.ring}, 0px 1px 2px 0px rgba(0,0,0,0.05)",
          },
          red: {
            value:
              "inset 0 0 0 1px {colors.pill.red.ring}, 0px 1px 2px 0px rgba(0,0,0,0.05)",
          },
          gray: {
            value:
              "inset 0 0 0 1px {colors.pill.gray.ring}, 0px 1px 2px 0px rgba(0,0,0,0.05)",
          },
          yellow: {
            value:
              "inset 0 0 0 1px {colors.pill.yellow.ring}, 0px 1px 2px 0px rgba(0,0,0,0.05)",
          },
        },
        button: {
          solid: {
            DEFAULT: {
              value: `
0px 1px 2px 0px #0A0D120D,
0px -4px 0px 0px #0A0D120D inset,
0px -1px 0px 2px #0A0D122E inset
`,
            },
            active: {
              value: `
0px 1px 2px 0px colorPalette.solid/70,
0px 0px 0px 1px colorPalette.solid
`,
            },
          },
          surface: {
            DEFAULT: {
              value: `
0px 1px 2px 0px #0000000D
`,
            },
          },
        },
        icon: {
          circle: {
            DEFAULT: {
              value: `
0px 1px 3px 0px #0000001A,
0px 1px 2px -1px #0000001A
`,
            },

            flat: {
              value: "none",
            },
          },
        },
      },
    },
    semanticTokens: {
      colors: {
        accent: {
          DEFAULT: {
            value: {
              base: "{colors.octaneAccent}",
              _addie: "{colors.addieAccent}",
            },
          },
          950: {
            value: {
              base: "{colors.octaneAccent.950}",
              _addie: "{colors.addieAccent.950}",
            },
          },
        },
        navy: {
          DEFAULT: {
            value: {
              base: "{colors.octaneNavy}",
              _addie: "{colors.addieNavy}",
            },
          },
          950: {
            value: {
              base: "{colors.octaneNavy.950}",
              _addie: "{colors.addieNavy.950}",
            },
          },
        },
        bg: {
          DEFAULT: { value: "{colors.white}" },
          page: { value: "{colors.pageBg}" },
          subtle: { value: "{colors.gray.50}" },
          muted: { value: "{colors.gray.100}" },
          hover: { value: "{colors.gray.100}" },
          emphasized: { value: "{colors.gray.200}" },
          inverted: { value: "{colors.black}" },
          panel: { value: "{colors.white}" },
          error: { value: "{colors.red.50}" },
          warning: { value: "{colors.orange.50}" },
          success: { value: "{colors.green.50}" },
          info: { value: "{colors.blue.50}" },
          solid: { value: "{colors.white}" },
        },
        border: {
          DEFAULT: { value: "{colors.gray.200}" },
          // NOTE: inline value — a raw `borderSoft` token would emit the
          // same --chakra-colors-border-soft variable and self-collide.
          soft: { value: "rgb(233,234,235)" },
          subtle: { value: "{colors.gray.50}" },
          muted: { value: "{colors.gray.100}" },
          emphasized: { value: "{colors.gray.300}" },
          inverted: { value: "{colors.gray.800}" },
          error: { value: "{colors.red.500}" },
          warning: { value: "{colors.orange.500}" },
          success: { value: "{colors.green.500}" },
          info: { value: "{colors.blue.500}" },
        },
        fg: {
          DEFAULT: { value: "{colors.gray.900}" },
          // Chakra's gray.950 is #111111, not the design's ink.
          ink: { value: "#09090B" },
          text2: { value: "{colors.text2}" },
          accent: { value: "{colors.navy}" },
          muted: { value: "{colors.gray.500}" },
          faded: { value: "{colors.faded}" },
          crumb: { value: "{colors.crumb}" },
          tableHead: { value: "{colors.tableHead}" },
          solid: { value: "{colors.white}" },
        },
        blue: {
          contrast: { value: "{colors.white}" },
          fg: { value: "{colors.accent}" },
          solid: { value: "{colors.accent}" },
        },
      },
      radii: {
        l1: { value: "0.375rem" },
        l2: { value: "0.5rem" },
        l3: { value: "0.75rem" },
      },
      shadows: {
        focusRing: {
          value: {
            base: "0 0 0 1px {colors.octaneAccent}, 0px 1px 2px 0px rgba(73,136,245,0.4)",
            _addie:
              "0 0 0 1px {colors.addieAccent}, 0px 1px 2px 0px rgba(107,125,46,0.4)",
          },
        },
        choiceSelected: {
          value: {
            base: "inset 0 0 0 2px {colors.octaneAccent}, 0px 1px 2px 0px rgba(0,0,0,0.05)",
            _addie:
              "inset 0 0 0 2px {colors.addieAccent}, 0px 1px 2px 0px rgba(0,0,0,0.05)",
          },
        },
      },
    },
    textStyles: {
      h1: {
        value: {
          color: "{colors.fg.ink}",
          fontSize: "22px",
          fontWeight: "500",
          lineHeight: "1.35",
        },
      },
      h2: {
        value: {
          color: "{colors.fg.ink}",
          fontSize: "18px",
          fontWeight: "500",
          lineHeight: "1.35",
        },
      },
      metricNumber: {
        value: {
          fontSize: "24px",
          fontVariantNumeric: "tabular-nums",
          fontWeight: "500",
          letterSpacing: "-0.02em",
          lineHeight: "1",
        },
      },
      tableHeader: {
        value: {
          color: "{colors.fg.tableHead}",
          fontFamily: "{fonts.inter}",
          fontSize: "12px",
          fontWeight: "500",
          lineHeight: "18px",
        },
      },
      pill: {
        value: {
          fontFamily: "{fonts.poppins}",
          fontSize: "12px",
          fontWeight: "500",
          lineHeight: "16px",
        },
      },
    },
    recipes: {
      button: buttonRecipe,
      input: inputRecipe,
      textarea: textareaRecipe,
    },
    slotRecipes: {
      dialog: dialogRecipe,
      menu: menuRecipe,
      select: selectRecipe,
    },
  },
});

const system = createSystem(defaultConfig, config);
export default system;

export const Provider = (props: PropsWithChildren) => (
  <ChakraProvider value={system}>{props.children}</ChakraProvider>
);
